/**
 * The **loadout pick-lock affordance** the equip-pick leaves (`<WeaponSlotEditor>`'s
 * weapon + mod pickers, `<SystemEditor>`'s system picker) and the brutalist desktop
 * editor both render — a thin display layer over the SAME single-source predicate the
 * reducer wall uses, `checkLoadoutInvariants(nextMech, pilot, compendium)` from
 * `@lancer-vtt/domain`. So the grey-out can never drift from the wall: the reducer
 * (`equip_weapon`/`equip_system`/`attach_mod`) still hard-rejects a build that would
 * install a 2nd copy of a Unique item, a 2nd AI system, or a Superheavy weapon with no
 * mount free to brace; this just makes the player see *why* before clicking.
 *
 * **Reuse, never re-derive (the core invariant).** This module does NOT re-implement
 * the Unique/AI/Superheavy rules. It builds a *tentative* `nextMech` per candidate —
 * exactly what the reducer builds before calling the seam — and asks the SAME
 * `checkLoadoutInvariants` whether the result is legal. A `unique`/`ai`/`superheavy`
 * violation is surfaced as a lock; the SP tenant (also folded into that seam) is left to
 * the existing SP affordances, which already filter unaffordable picks out of the
 * pickers, so it never double-surfaces.
 */
import {
  checkLoadoutInvariants,
  composedMounts,
  composedMountProvenance,
  type LoadoutData,
  type LoadoutInvariantCompendium,
  type MechData,
  type PilotData,
} from "@lancer-vtt/domain";

/**
 * A would-be addition to test: a weapon or mod into a specific (still-empty) mount slot,
 * or a system appended to the loadout. Mirrors the three equip commands' targets.
 */
export type LoadoutCandidate =
  | { surface: "weapon"; mountIdx: number; slotIdx: number; id: string }
  | { surface: "mod"; mountIdx: number; slotIdx: number; id: string }
  | { surface: "system"; id: string };

/** The reason-label a non-SP violation renders under (id-free, player-facing). */
const KIND_LABEL: Record<"unique" | "ai" | "superheavy" | "flex", string> = {
  unique: "Unique — only one per mech",
  ai: "AI — only one per mech",
  superheavy: "Superheavy — needs another mount free to brace",
  flex: "Flexible — one Main weapon OR up to two Auxiliary, not both",
};

/**
 * Place the candidate into the mech's **active** loadout, returning a new `MechData`
 * (structural copy — only the touched loadout/mount/slot is rebuilt). The reducer
 * builds an equivalent `nextMech` before calling the invariant; this is the affordance's
 * tentative twin. A frameless mech (no active loadout) is returned unchanged.
 *
 * A weapon/mod candidate's `mountIdx` is a **composed** index — the same one
 * `equip_weapon`/`attach_mod` address — so this mirrors the reducer's
 * `requireComposedSlot`/`writeComposedSlot` mapping exactly: an index below
 * `loadout.mounts.length` writes straight into native storage (never through the
 * composed mount's presented `type`, which may carry a retrofit-in-place swap that
 * isn't real storage); an index at/above it writes into the granting bonus mount's
 * own dedicated storage field (`composedMountProvenance` disambiguates which).
 *
 * **Exported for the pair-agreement pin** (`loadoutPickLock.test.ts`) — the test builds
 * the SAME candidate through this function and through the reducer's `equip_weapon`
 * (which calls the sibling `writeComposedSlot` in `@lancer-vtt/domain`) and asserts the
 * two results agree. Not otherwise a public API — every other caller should go through
 * `loadoutPickLock`.
 */
export function withCandidate(args: {
  mech: MechData;
  pilot: PilotData;
  compendium: LoadoutInvariantCompendium;
  candidate: LoadoutCandidate;
}): MechData {
  const { mech, pilot, compendium, candidate } = args;
  const idx = mech.activeLoadoutIndex;
  const loadout = mech.loadouts[idx];
  if (!loadout) return mech;

  if (candidate.surface === "system") {
    const nextLoadout: LoadoutData = { ...loadout, systems: [...(loadout.systems ?? []), candidate.id] };
    return { ...mech, loadouts: mech.loadouts.map((l, i) => (i === idx ? nextLoadout : l)) };
  }

  const composed = composedMounts(loadout, pilot, compendium);
  const mount = composed[candidate.mountIdx];
  const priorSlot = mount?.slots[candidate.slotIdx];
  // An out-of-range composed index (a bonus mount the pilot doesn't currently hold, or
  // an over-cap one) has nothing to place the candidate into — same "no lock" outcome
  // as any other candidate the picker would never actually offer.
  if (!mount || !priorSlot) return mech;

  const nextSlot =
    candidate.surface === "weapon"
      ? { ...priorSlot, weaponId: candidate.id }
      : { ...priorSlot, modId: candidate.id };

  let nextLoadout: LoadoutData;
  if (candidate.mountIdx < loadout.mounts.length) {
    // Write through `mount.slots` (the COMPOSED, normalized array resolved
    // above) so a stored 1-slot Flex mount's not-yet-materialized slot 1 is
    // reachable here too — mapping over the STORED `m.slots` directly would
    // silently no-op the candidate for the same reason `reducer.ts`'s
    // `writeComposedSlot` used to (see its doc-comment). The written-back mount
    // keeps the STORED mount `m`'s own `type` (never `mount`'s, which may carry
    // a retrofit-in-place override that isn't real storage — see this
    // function's doc-comment above).
    const nextSlots = mount.slots.map((s, si) => (si === candidate.slotIdx ? nextSlot : s));
    nextLoadout = {
      ...loadout,
      mounts: loadout.mounts.map((m, mi) => (mi !== candidate.mountIdx ? m : { ...m, slots: nextSlots })),
    };
  } else {
    const provenance = composedMountProvenance(loadout, pilot, compendium)[candidate.mountIdx];
    // Unreachable in practice — `composed`/`composedMountProvenance` derive from the
    // same internal walk (`bonusMountTail`), so a resolved tail `mount` always has a
    // matching provenance. Defensive no-op mirrors `reducer.ts`'s `writeComposedSlot`.
    if (!provenance) return mech;
    nextLoadout = {
      ...loadout,
      [provenance.storageField]: {
        ...mount,
        slots: mount.slots.map((s, si) => (si === candidate.slotIdx ? nextSlot : s)),
      },
    };
  }

  return { ...mech, loadouts: mech.loadouts.map((l, i) => (i === idx ? nextLoadout : l)) };
}

/**
 * `null` when adding the candidate keeps the loadout legal (w.r.t. Unique/AI/Superheavy);
 * otherwise the lock with a player-facing reason the picker shows on the greyed, disabled
 * option. Mirrors `checkLoadoutInvariants` exactly — same predicate the reducer's equip
 * rules call — so a locked pick the leaf greys is precisely the install the reducer would
 * hard-reject. The SP tenant is intentionally ignored here (the SP affordances own it).
 *
 * Note the Superheavy tenant locks two distinct picks off the one predicate: a Superheavy
 * weapon when no other mount is free to brace, *and* an ordinary weapon that would fill the
 * last free mount while a Superheavy is mounted (consuming its brace).
 */
export function loadoutPickLock(args: {
  pilot: PilotData;
  mech: MechData;
  candidate: LoadoutCandidate;
  compendium: LoadoutInvariantCompendium;
}): { label: string } | null {
  const { pilot, mech, candidate, compendium } = args;
  const violation = checkLoadoutInvariants(withCandidate({ mech, pilot, compendium, candidate }), pilot, compendium);
  if (
    violation &&
    (violation.kind === "unique" ||
      violation.kind === "ai" ||
      violation.kind === "superheavy" ||
      violation.kind === "flex")
  ) {
    return { label: KIND_LABEL[violation.kind] };
  }
  return null;
}
